/**
 * @fileoverview Vuex module para dados do usuário logado.
 *
 * Gerencia o estado do usuário atual, incluindo:
 * - Dados completos do usuário (perfil, permissões)
 * - Unidade administrativa selecionada (multi-setor)
 * - Endereço do usuário
 *
 * A unidade administrativa selecionada é persistida no localStorage
 * e restaurada ao recarregar a página.
 *
 * @module vuex/me
 * @see {@link module:services/UserService} para chamadas de API
 * @see {@link module:services/AdminUnitSwitchService} para troca de setor
 */

import {omit} from 'lodash';
import userService from '@/services/UserService';
import addressService from '@/services/AddressService';

/**
 * Recupera unidade administrativa do localStorage.
 *
 * @returns {number|null} ID da unidade ou null se não houver
 */
export const getSelectedAdministrativeUnitFromStorage = () => !!localStorage.getItem('selectedAdministrativeUnit') && !Number.isNaN(localStorage.getItem('selectedAdministrativeUnit'))
  ? Number(localStorage.getItem('selectedAdministrativeUnit'))
  : null;

/**
 * Vuex module do usuário logado.
 *
 * @namespace me
 * @property {Object} state - Estado do módulo
 * @property {UserComplete} state.user - Dados do usuário
 * @property {number|null} state.selectedAdministrativeUnit - ID do setor ativo
 */
const me = {
  state: {
    user: {},
    selectedAdministrativeUnit: getSelectedAdministrativeUnitFromStorage(),
  },
  getters: {
    user(state) {
      return state.user;
    },
    selectedAdministrativeUnit(state) {
      console.log('Selected Administrative Unit:', state.selectedAdministrativeUnit);
      return state.selectedAdministrativeUnit;
    },
    userAdministrativeUnits(state) {
      return state.user && state.user.administrative_units ? state.user.administrative_units : [];
    },
  },
  mutations: {
    setUser(state, payload) {
      state.user = {...state.user, ...payload};

      // Verificar se o login foi recente (tela de login ou atualização da página)
      const lastLoginTime = localStorage.getItem('last_login_time');
      const currentTime = new Date().getTime();
      const isRecentLogin = !lastLoginTime || (currentTime - parseInt(lastLoginTime)) > 60000; // 1 minuto

      // Verificar se há uma unidade administrativa salva no localStorage
      const savedAdminUnit = localStorage.getItem('selectedAdministrativeUnit');

      if (!savedAdminUnit) {
        state.selectedAdministrativeUnit = payload.administrative_unit.id;
        localStorage.setItem('selectedAdministrativeUnit', state.selectedAdministrativeUnit);
      } else {
        state.selectedAdministrativeUnit = parseInt(savedAdminUnit);

        // Se a unidade salva não existir nas unidades do usuário, redefine para a principal
        if (!payload.administrative_units.some(unit => unit.id === state.selectedAdministrativeUnit)) {
          state.selectedAdministrativeUnit = payload.administrative_unit ? payload.administrative_unit.id : payload.administrative_units[0].id;

          console.error('Unidade administrativa salva não encontrada entre as unidades do usuário. Redefinindo para a principal.');
          // localStorage.setItem('selectedAdministrativeUnit', state.selectedAdministrativeUnit);
        }
      }

      // Se o usuário tem unidades administrativas e nenhuma está selecionada, seleciona a principal ou a salva
      /* if (payload.administrative_units && payload.administrative_units.length > 0 && !state.selectedAdministrativeUnit) {
         if (isRecentLogin || !savedAdminUnit) {
           // Para logins recentes, sempre usa a unidade principal
           if (payload.administrative_unit) {
             state.selectedAdministrativeUnit = payload.administrative_unit.id;
             localStorage.setItem('selectedAdministrativeUnit', payload.administrative_unit.id);
           } else {
             state.selectedAdministrativeUnit = payload.administrative_units[0].id;
             localStorage.setItem('selectedAdministrativeUnit', payload.administrative_units[0].id);
           }

           // Atualiza o timestamp do último login
           localStorage.setItem('last_login_time', currentTime.toString());
         }
         else if (savedAdminUnit && payload.administrative_units.some(unit => unit.id === parseInt(savedAdminUnit))) {
           // Se não é login recente e tiver uma unidade salva válida, usa ela
           state.selectedAdministrativeUnit = parseInt(savedAdminUnit);
         } else {
           // Caso contrário, usa a unidade principal ou a primeira da lista
           state.selectedAdministrativeUnit = payload.administrative_unit ?
             payload.administrative_unit.id :
             payload.administrative_units[0].id;
           localStorage.setItem('selectedAdministrativeUnit', state.selectedAdministrativeUnit);
         }
       }
    */
    },
    setSelectedAdministrativeUnit(state, unitId) {
      state.selectedAdministrativeUnit = unitId;
    },
    setUserAddress(state, payload) {
      // if address come from viacep api
      let address;
      if (payload.logradouro) {
        address = {
          street: payload.logradouro,
          complement: payload.complemento,
          neighborhood: payload.bairro,
          city: payload.localidade,
          state: payload.uf,
          zipcode: payload.cep,
        };
      } else {
        address = {...payload};
      }
      state.user = {...state.user, address};
    },
    logout(state) {
      state.user = {};
    },
  },
  actions: {
    async getUser({commit}) {
      const response = await userService.getUser();
      commit('setUser', response.data);
      localStorage.setItem('user', JSON.stringify(response.data));
    },
    // Método simplificado que delega para o AdminUnitSwitchService
    async setSelectedAdministrativeUnit({commit}, unitId) {
      // Esta ação é mantida para compatibilidade com código existente,
      // mas a implementação foi movida para AdminUnitSwitchService

      // Importação estática do serviço para evitar problemas com imports dinâmicos
      const AdminUnitSwitchService = require('@/services/AdminUnitSwitchService').default;

      try {
        return await AdminUnitSwitchService.switchAdminUnit(unitId);
      } catch (error) {
        console.error('Erro na ação setSelectedAdministrativeUnit:', error);
        return Promise.reject(error);
      }
    },
    updateUser({commit}, payload) {
      if (payload.avatar && !payload.avatar.url) {
        commit('toggleLoader', {show: true, component: 'UserAvatar'});
      } else if (payload.signature && !payload.signature.url) {
        commit('toggleLoader', {show: true, component: 'UserSignature'});
      } else {
        commit('toggleLoader', {show: true});
      }
      userService.updateUser({id: payload.userId, data: omit(payload, ['userId'])})
        .then(() => {
          commit('toggleSnackbar', {
            show: true,
            timeout: 6000,
            color: 'success',
            message: 'Usuário atualizado com sucesso!',
            messageIcon: 'mdi-check',
          });
          if (payload.avatar && !payload.avatar.url) {
            commit('setUser', {avatar: {url: payload.avatar}});
          }
          if (payload.signature && !payload.signature.url) {
            commit('setUser', {signature: {url: payload.signature}});
          }
          commit('toggleLoader', {show: false});
        })
        .catch((error) => {
          commit('toggleSnackbar', {
            show: true,
            timeout: 6000,
            color: 'error',
            message: error.message,
            messageIcon: 'mdi-alert-outline',
          });
          commit('toggleLoader', {show: false});
        });
    },
    getUserCepAddress({commit}, payload) {
      commit('toggleLoader', {show: true});
      addressService.searchZipCode(payload)
        .then((response) => {
          response.json()
            .then((result) => {
              commit('setUserAddress', result);
              commit('toggleLoader', {show: false});
            });
        })
        .catch((error) => {
          commit('toggleLoader', {show: false});
          commit('toggleSnackbar', {
            show: true,
            timeout: 6000,
            color: 'error',
            message: error.message,
            messageIcon: 'mdi-alert-outline',
          });
        });
    },
    logout({commit}) {
      commit('logout');
    },
  },
};

export default me;



// WEBPACK FOOTER //
// ./src/vuex/account/me.js